import type { IMPORT_STATUS } from 'app/common/types';
import { createRoutine as createNewRoutine } from 'app/common/utils/new-routines';
import { createRoutine } from 'app/common/utils/routines';
import { createAction as createTypesafeAction } from 'typesafe-actions';
import {
  CREATE_PROJECT,
  DELETE_PROJECT,
  GET_PROJECT,
  GET_PROJECT_LIST,
  GET_PROJECT_MEMBERS,
  IMPORT_PROJECT_FILES,
  IMPORT_PROJECT_URI,
  INVITE_USER,
  REMOVE_PROJECT_MEMBER,
  REMOVE_SELECTED_PROJECT_MEMBERS,
  SET_CURRENT_PROJECT,
  SET_IMPORT_STATUS,
  UPDATE_INVITE_STATUS,
  UPDATE_PROJECT_PERMISSIONS,
  UPLOAD_IMPORT_COMPLETE,
  URI_IMPORT_COMPLETE
} from './action-types';
import type { UpdatePermissionParams } from './components/members/role-dropdown-cell/role-dropdown-cell.js';
import { NAME } from './constants.js';
import type { Project } from './types.js';

export function getProjectList() {
  return {
    type: `${GET_PROJECT_LIST}_REQUESTED`
  };
}

export type SetProjectPayload = {
  projectId: string;
  branch: string;
};

export const setCurrentProject = createTypesafeAction(SET_CURRENT_PROJECT)<SetProjectPayload>();

export function getProjectMembers() {
  return {
    type: `${GET_PROJECT_MEMBERS}_REQUESTED`
  };
}

export function updateProjectPermissions({ userId, email, status, role }: Partial<UpdatePermissionParams> = {}) {
  return {
    type: UPDATE_PROJECT_PERMISSIONS,
    payload: {
      userId,
      email,
      status,
      role
    }
  };
}

export type RemoveProjectMemberParams = {
  userId: string;
  email?: string;
};

export const removeProjectMember = createNewRoutine(REMOVE_PROJECT_MEMBER)<
  undefined,
  RemoveProjectMemberParams,
  undefined,
  undefined,
  string,
  undefined
>();

export function removeSelectedProjectMembers() {
  return {
    type: REMOVE_SELECTED_PROJECT_MEMBERS
  };
}

type DeleteProjectParams = {
  projectId: string;
  redirectOnSuccess: string;
  redirectOnFailure: string;
};

export function deleteProject({ projectId, redirectOnSuccess, redirectOnFailure }: Partial<DeleteProjectParams> = {}) {
  return {
    type: `${DELETE_PROJECT}_REQUESTED`,
    payload: {
      projectId
    },
    meta: {
      redirectOnSuccess,
      redirectOnFailure
    }
  };
}

type InviteUserParams = {
  email: string;
  role: string;
  projectId: string;
};

export function inviteUser({ email, role, projectId }: InviteUserParams) {
  return {
    type: INVITE_USER,
    meta: {
      projectId
    },
    payload: {
      email,
      role
    }
  };
}

type UpdateInviteStatusParams = {
  inviteCode: string;
  projectId: string;
  status: string;
};

export function updateInviteStatus({ inviteCode, projectId, status }: UpdateInviteStatusParams) {
  return {
    type: UPDATE_INVITE_STATUS,
    payload: {
      inviteCode,
      projectId,
      status
    }
  };
}

export function setImportStatus(status: IMPORT_STATUS) {
  return {
    type: SET_IMPORT_STATUS,
    payload: status
  };
}

export function uploadImportComplete(projectId: string) {
  return {
    type: UPLOAD_IMPORT_COMPLETE,
    payload: projectId
  };
}

export function uriImportComplete(projectId: string) {
  return {
    type: URI_IMPORT_COMPLETE,
    payload: projectId
  };
}

export type GetProjectRequestedPayload = {
  projectId: string;
};

export type GetProjectFulfilledPayload = {
  project: Project;
};

export const getProject = createNewRoutine(GET_PROJECT)<
  undefined,
  GetProjectRequestedPayload,
  undefined,
  GetProjectFulfilledPayload,
  string,
  undefined
>();

export const updateProjectSettings = createRoutine(`${NAME}/UPDATE_PROJECT_SETTINGS`);
export const getProjectAdmins = createRoutine(`${NAME}/GET_PROJECT_ADMINS`);
export const createProject = createRoutine(CREATE_PROJECT);
export const importProjectURI = createRoutine(IMPORT_PROJECT_URI);
export const importProjectFiles = createRoutine(IMPORT_PROJECT_FILES);
